import React from 'react';
import { useTranslation } from 'react-i18next';

import { Switch } from '@core-ui/Components/ui/switch';
import { Label } from '@core-ui/Components/ui/label';
import { cn } from '@core-ui-lib/utils';

import { Shuffle, PhoneOff, PhoneCall } from 'lucide-react';

import useWizardStore from '../../store/useWizardStore';
import { OngoingCallAction } from '../../types/wizard.types';

export default function SessionSettingsStep (): React.JSX.Element {
  const { t } = useTranslation();
  const {
    shuffleContacts,
    setShuffleContacts,
    ongoingCallAction,
    setOngoingCallAction,
  } = useWizardStore();

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-semibold">{t('wizard.sessionSettings.title')}</h3>
        <p className="text-sm text-muted-foreground">{t('wizard.sessionSettings.description')}</p>
      </div>

      <div className="space-y-6">
        {/* Shuffle Contacts Setting */}
        <div className="rounded-lg border border-border p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-start gap-4">
              <div className={cn(
                'flex h-10 w-10 items-center justify-center rounded-lg',
                shuffleContacts ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
              )}>
                <Shuffle className="h-5 w-5" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="shuffle-toggle" className="text-base font-medium cursor-pointer">
                  {t('wizard.sessionSettings.shuffle.title')}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t('wizard.sessionSettings.shuffle.description')}
                </p>
              </div>
            </div>
            <Switch
              id="shuffle-toggle"
              checked={shuffleContacts}
              onCheckedChange={setShuffleContacts}
            />
          </div>
        </div>

        {/* Ongoing Calls Behavior Setting */}
        <div className="rounded-lg border border-border p-5">
          <div className="mb-4">
            <h4 className="text-base font-medium">{t('wizard.sessionSettings.cutOff.title')}</h4>
            <p className="text-sm text-muted-foreground mt-1">
              {t('wizard.sessionSettings.cutOff.description')}
            </p>
          </div>

          <div className="space-y-3">
            {/* Cut Off Option */}
            <button
              type="button"
              onClick={() => {
                setOngoingCallAction(OngoingCallAction.CUT_OFF);
              }}
              className={cn(
                'w-full flex items-start gap-4 p-4 rounded-lg border-2 transition-all text-left',
                ongoingCallAction === OngoingCallAction.CUT_OFF
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50 hover:bg-muted/40',
              )}
            >
              <div className={cn(
                'h-5 w-5 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center',
                ongoingCallAction === OngoingCallAction.CUT_OFF
                  ? 'border-primary bg-primary'
                  : 'border-muted-foreground',
              )}>
                {ongoingCallAction === OngoingCallAction.CUT_OFF && (
                  <div className="h-2 w-2 rounded-full bg-white" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <PhoneOff className={cn(
                    'h-4 w-4',
                    ongoingCallAction === OngoingCallAction.CUT_OFF
                      ? 'text-primary'
                      : 'text-muted-foreground',
                  )} />
                  <span className="font-medium">{t('wizard.sessionSettings.cutOff.optionCutOff')}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('wizard.sessionSettings.cutOff.optionCutOffDesc')}
                </p>
              </div>
            </button>

            {/* Hold Option */}
            <button
              type="button"
              onClick={() => {
                setOngoingCallAction(OngoingCallAction.HOLD);
              }}
              className={cn(
                'w-full flex items-start gap-4 p-4 rounded-lg border-2 transition-all text-left',
                ongoingCallAction === OngoingCallAction.HOLD
                  ? 'border-primary bg-primary/5'
                  : 'border-border bg-muted/40',
              )}
              disabled={true}
            >
              <div className={cn(
                'h-5 w-5 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center',
                ongoingCallAction === OngoingCallAction.HOLD
                  ? 'border-primary bg-primary'
                  : 'border-muted-foreground',
              )}>
                {ongoingCallAction === OngoingCallAction.HOLD && (
                  <div className="h-2 w-2 rounded-full bg-white" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <PhoneCall className={cn(
                    'h-4 w-4',
                    ongoingCallAction === OngoingCallAction.HOLD
                      ? 'text-primary'
                      : 'text-muted-foreground',
                  )} />
                  <span className="font-medium">{t('wizard.sessionSettings.cutOff.optionHold')}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('wizard.sessionSettings.cutOff.optionHoldDesc')}
                </p>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
